// ============================================================================
// AppointmentScheduleSettingsDialog — рабочие часы/шаг сетки для расчёта
// свободных окон. Настройка пользователя (settingsStore), данные записей не
// меняются: занятость всегда читается с сервера.
// ============================================================================

import { useEffect, useState } from 'react';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { Button } from '@/components/ui/button';
import type { AppointmentScheduleSettings } from '@/lib/medicAppointments';

const WEEKDAYS: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Пн' },
  { value: 2, label: 'Вт' },
  { value: 3, label: 'Ср' },
  { value: 4, label: 'Чт' },
  { value: 5, label: 'Пт' },
  { value: 6, label: 'Сб' },
  { value: 0, label: 'Вс' },
];

export interface AppointmentScheduleSettingsDialogProps {
  open: boolean;
  settings: AppointmentScheduleSettings;
  onClose: () => void;
  onSave: (settings: AppointmentScheduleSettings) => void;
}

export function AppointmentScheduleSettingsDialog({
  open, settings, onClose, onSave,
}: AppointmentScheduleSettingsDialogProps) {
  const [draft, setDraft] = useState<AppointmentScheduleSettings>(settings);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setDraft(settings);
      setError('');
    }
  }, [open, settings]);

  const update = <K extends keyof AppointmentScheduleSettings>(key: K, value: AppointmentScheduleSettings[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const toggleDay = (day: number, checked: boolean) => {
    const next = checked
      ? [...new Set([...draft.workdays, day])].sort((a, b) => a - b)
      : draft.workdays.filter((value) => value !== day);
    update('workdays', next);
  };

  const save = () => {
    if (!draft.workdayStart || !draft.workdayEnd) {
      setError('Укажите начало и окончание рабочего дня.');
      return;
    }
    if (draft.workdayStart >= draft.workdayEnd) {
      setError('Начало рабочего дня должно быть раньше окончания.');
      return;
    }
    if (draft.breakStart && draft.breakEnd && draft.breakStart >= draft.breakEnd) {
      setError('Начало перерыва должно быть раньше окончания.');
      return;
    }
    if (draft.workdays.length === 0) {
      setError('Выберите хотя бы один рабочий день.');
      return;
    }
    if (!Number.isFinite(draft.slotMinutes) || draft.slotMinutes < 5 || draft.slotMinutes > 120) {
      setError('Шаг сетки — от 5 до 120 минут.');
      return;
    }
    if (!Number.isFinite(draft.leadMinutes) || draft.leadMinutes < 0 || draft.leadMinutes > 1440) {
      setError('Запас до начала — от 0 до 1440 минут.');
      return;
    }
    setError('');
    onSave(draft);
    onClose();
  };

  return (
    <UniversalWindow
      open={open}
      onClose={onClose}
      title="Сетка свободных окон"
      width="w-[min(560px,94vw)]"
      height="h-auto max-h-[90vh]"
      resizable={false}
      topBar={(
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-7 px-2" onClick={onClose}>Отмена</Button>
          <Button size="sm" className="h-7 px-2" onClick={save}>Применить</Button>
        </div>
      )}
    >
      <div className="space-y-4 overflow-auto p-4">
        <div className="rounded-lg border bg-muted/20 px-3 py-2 text-[11px] text-muted-foreground">
          Рабочие часы нужны только для построения сетки. Занятость окон берётся из
          серверных записей выбранного специалиста; сервер дополнительно проверяет
          пересечения для психологических записей.
        </div>

        <section className="grid gap-3 sm:grid-cols-2">
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Начало дня</span>
            <input
              type="time"
              className="h-8 w-full rounded-md border bg-background px-2"
              value={draft.workdayStart}
              onChange={(event) => update('workdayStart', event.target.value)}
            />
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Окончание дня</span>
            <input
              type="time"
              className="h-8 w-full rounded-md border bg-background px-2"
              value={draft.workdayEnd}
              onChange={(event) => update('workdayEnd', event.target.value)}
            />
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Перерыв с</span>
            <input
              type="time"
              className="h-8 w-full rounded-md border bg-background px-2"
              value={draft.breakStart}
              onChange={(event) => update('breakStart', event.target.value)}
            />
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Перерыв по</span>
            <input
              type="time"
              className="h-8 w-full rounded-md border bg-background px-2"
              value={draft.breakEnd}
              onChange={(event) => update('breakEnd', event.target.value)}
            />
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Шаг сетки</span>
            <select
              className="h-8 w-full rounded-md border bg-background px-2"
              value={draft.slotMinutes}
              onChange={(event) => update('slotMinutes', Number(event.target.value))}
            >
              {[15, 20, 30, 45, 60].map((minutes) => (
                <option key={minutes} value={minutes}>{minutes} минут</option>
              ))}
            </select>
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Не показывать окна ближе чем за</span>
            <select
              className="h-8 w-full rounded-md border bg-background px-2"
              value={draft.leadMinutes}
              onChange={(event) => update('leadMinutes', Number(event.target.value))}
            >
              {[0, 15, 30, 60, 120, 240].map((minutes) => (
                <option key={minutes} value={minutes}>{minutes === 0 ? 'без запаса' : `${minutes} минут`}</option>
              ))}
            </select>
          </label>
          <div className="space-y-1 text-xs">
            <span className="text-muted-foreground">Длительность (медицина)</span>
            <div className="flex h-8 items-center rounded-md border bg-muted/30 px-2 text-muted-foreground">
              30 минут (фиксировано)
            </div>
          </div>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Длительность (психология)</span>
            <select
              className="h-8 w-full rounded-md border bg-background px-2"
              value={draft.durations.psy}
              onChange={(event) => update('durations', { ...draft.durations, psy: Number(event.target.value) })}
            >
              {[30, 45, 60, 90, 120].map((minutes) => (
                <option key={minutes} value={minutes}>{minutes} минут</option>
              ))}
            </select>
          </label>
        </section>

        <section>
          <div className="mb-1 text-xs text-muted-foreground">Рабочие дни</div>
          <div className="flex flex-wrap gap-1.5">
            {WEEKDAYS.map((day) => (
              <label
                key={day.value}
                className="flex cursor-pointer items-center gap-1.5 rounded-lg border px-2 py-1 text-[11px]"
              >
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-primary"
                  checked={draft.workdays.includes(day.value)}
                  onChange={(event) => toggleDay(day.value, event.target.checked)}
                />
                {day.label}
              </label>
            ))}
          </div>
        </section>

        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        )}
        <div className="text-[10px] text-muted-foreground">
          Настройка сохраняется в личных настройках пользователя и не меняет формы/данные базы.
        </div>
      </div>
    </UniversalWindow>
  );
}
